import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Workplace, Visit } from '../../types';
import { visitApi, workplaceApi } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import Swal from 'sweetalert2';
import './PlanVisitModal.css';

interface PlanVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  workplace?: Workplace | null;
  workplacesList?: Workplace[];
  initialDate?: string;
  allowChangeWorkplace?: boolean;
  onVisitCreated: (newVisit: Visit) => void;
}

const PlanVisitModal: React.FC<PlanVisitModalProps> = ({
  isOpen,
  onClose,
  workplace,
  workplacesList = [],
  initialDate,
  allowChangeWorkplace,
  onVisitCreated
}) => {
  // İşyeri değiştirilebilir mi? (Eğer açıkça belirtilmediyse: workplace varsa değiştirilemez, yoksa değiştirilebilir)
  const canChangeWorkplace = allowChangeWorkplace !== undefined ? allowChangeWorkplace : !workplace;
  // Bugünün tarihini YYYY-MM-DD olarak al
  const today = new Date().toISOString().split('T')[0];

  // İşyerleri Listesi (Eğer dışarıdan verilmediyse otomatik çekilir)
  const [allWorkplaces, setAllWorkplaces] = useState<Workplace[]>(workplacesList || []);
  const [selectedWp, setSelectedWp] = useState<Workplace | null>(workplace || null);

  // Akıllı Arama State'leri
  const [wpSearchTerm, setWpSearchTerm] = useState('');
  const [showWpSuggestions, setShowWpSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Ziyaret Form Alanları
  const [visitDate, setVisitDate] = useState(initialDate || today);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('11:00');
  const [visitType, setVisitType] = useState('Periyodik Saha Ziyareti (Rutin)');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [markAsCompleted, setMarkAsCompleted] = useState(false);

  // Geçmiş Tarih Kontrolü ve Gün Farkı Hesabı
  const isPastDate = Boolean(visitDate && visitDate < today);
  const pastDaysCount = useMemo(() => {
    if (!visitDate || visitDate >= today) return 0;
    const target = new Date(visitDate + 'T00:00:00');
    const now = new Date(today + 'T00:00:00');
    return Math.max(1, Math.round((now.getTime() - target.getTime()) / (1000 * 60 * 60 * 24)));
  }, [visitDate, today]);
  const pastDaysLabel = pastDaysCount === 1 ? '1 gün önce / Dün' : `${pastDaysCount} gün önce`;

  // İşyerlerini yükle (Eğer yoksa ve işyeri seçimi serbestse)
  useEffect(() => {
    if (isOpen && canChangeWorkplace) {
      if (workplacesList && workplacesList.length > 0) {
        setAllWorkplaces(workplacesList);
      } else {
        workplaceApi.getAll().then((res) => {
          if (res.success && res.data) {
            setAllWorkplaces(res.data);
          }
        }).catch((err) => console.error('İşyerleri listesi yüklenemedi:', err));
      }
    }
  }, [isOpen, workplacesList, canChangeWorkplace]);

  // Modal her açıldığında varsayılanları ayarla
  useEffect(() => {
    if (isOpen) {
      if (workplace) {
        setSelectedWp(workplace);
      } else {
        setSelectedWp(null);
      }
      if (initialDate) {
        setVisitDate(initialDate);
      }
      setWpSearchTerm('');
      setShowWpSuggestions(false);
    }
  }, [isOpen, workplace, initialDate]);

  // Akıllı Arama Önerileri
  const suggestions = useMemo(() => {
    const q = wpSearchTerm.toLocaleLowerCase('tr-TR').trim();
    if (!q) {
      // Arama boşken ilk 12 işyerini listele
      return allWorkplaces.slice(0, 12);
    }
    return allWorkplaces.filter((wp) => {
      const title = (wp.title || '').toLocaleLowerCase('tr-TR');
      const sgk = (wp.sgkNo || '').toLocaleLowerCase('tr-TR');
      const city = (wp.city || '').toLocaleLowerCase('tr-TR');
      const district = (wp.district || '').toLocaleLowerCase('tr-TR');
      return title.includes(q) || sgk.includes(q) || city.includes(q) || district.includes(q);
    }).slice(0, 15);
  }, [allWorkplaces, wpSearchTerm]);

  if (!isOpen) return null;

  const handleSelectWorkplace = (wp: Workplace) => {
    setSelectedWp(wp);
    setWpSearchTerm('');
    setShowWpSuggestions(false);
  };

  const handleChangeSelectedWorkplace = () => {
    setSelectedWp(null);
    setWpSearchTerm('');
    setTimeout(() => {
      setShowWpSuggestions(true);
      searchInputRef.current?.focus();
    }, 50);
  };

  const getHazardBadge = (dc?: string) => {
    const s = (dc || '').toLowerCase().trim();
    if (s.includes('çok') || s.includes('cok') || s.includes('very')) {
      return { label: 'Çok Tehlikeli', color: '#b91c1c', bg: '#fef2f2', border: '#ef4444' };
    }
    if (s.includes('tehlike') || s.includes('dangerous')) {
      return { label: 'Tehlikeli', color: '#b45309', bg: '#fffbeb', border: '#f59e0b' };
    }
    return { label: 'Az Tehlikeli', color: '#047857', bg: '#ecfdf5', border: '#10b981' };
  };

  const saveVisit = async (allowConflict = false) => {
    if (!selectedWp) {
      Swal.fire({
        icon: 'warning',
        title: 'İşyeri Seçilmedi',
        text: 'Lütfen arama kutusunu kullanarak ziyaret planlanacak işyerini seçin.',
        confirmButtonColor: '#b45309'
      });
      return;
    }

    if (!visitDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Tarih Eksik',
        text: 'Lütfen bir ziyaret tarihi seçin.',
        confirmButtonColor: '#b45309'
      });
      return;
    }

    if (startTime >= endTime) {
      Swal.fire({
        icon: 'warning',
        title: 'Geçersiz Saat Aralığı',
        text: 'Bitiş saati, başlangıç saatinden sonra olmalıdır.',
        confirmButtonColor: '#b45309'
      });
      return;
    }

    const isPast = Boolean(visitDate && visitDate < today);

    // Geçmiş Tarih Onayı: Kullanıcıya geçmiş tarihli işlem teyidi sor
    if (isPast && !allowConflict) {
      const dayText = pastDaysCount === 1 ? 'dün' : `${pastDaysCount} gün önce`;
      const statusText = markAsCompleted ? 'Gerçekleşti ("Tamamlandı")' : 'Planlandı ("Bekleyen")';

      const confirmPast = await Swal.fire({
        icon: 'warning',
        title: '⚠️ Geçmiş Tarihli Ziyaret Kaydı',
        html: `
          <div style="text-align: left; font-size: 13px; line-height: 1.6; color: #1e293b;">
            <p style="margin: 0 0 8px 0;">
              <strong>${selectedWp.title}</strong> işyeri için <strong>${formatDate(visitDate)}</strong> (${dayText}) tarihine kayıt ekliyorsunuz.
            </p>
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 8px 12px; border-radius: 6px; margin-bottom: 8px;">
              📌 Kayıt Durumu: <strong>${statusText}</strong>
            </div>
            <p style="margin: 0; font-size: 12px; color: #64748b;">
              Bu geçmiş ziyaret kaydını onaylıyor musunuz?
            </p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: '✓ Evet, Kaydet',
        cancelButtonText: 'Tarihi Değiştir',
        confirmButtonColor: '#059669',
        cancelButtonColor: '#64748b'
      });

      if (!confirmPast.isConfirmed) {
        return;
      }
    }

    try {
      setSubmitting(true);
      const res = await visitApi.create({
        workplaceId: selectedWp.id,
        visitDate,
        startTime,
        endTime,
        visitType,
        notes,
        status: isPast && markAsCompleted ? 'completed' : 'planned',
        allowConflict
      });

      if (res.success && res.data) {
        const titleText = isPast && markAsCompleted ? 'Ziyaret Kaydedildi!' : 'Ziyaret Planlandı!';
        const bodyText = isPast && markAsCompleted
          ? `${formatDate(visitDate)} tarihli saha ziyareti "Tamamlandı" olarak sisteme işlendi.`
          : `${formatDate(visitDate)} tarihli (${startTime} - ${endTime}) saha ziyareti başarıyla kaydedildi.`;

        Swal.fire({
          icon: 'success',
          title: titleText,
          text: bodyText,
          timer: 2000,
          showConfirmButton: false
        });
        onVisitCreated(res.data);
        onClose();
      }
    } catch (err: any) {
      console.error('Ziyaret planlama hatası:', err);

      // ÇAKIŞMA TESPİTİ (HTTP 409 veya conflict bayrağı)
      if (err.response?.status === 409 || err.response?.data?.conflict) {
        const details = err.response?.data?.conflictDetails;
        const conflictDateStr = formatDate(details?.date || visitDate);
        const conflictHoursStr = `${details?.startTime || ''} - ${details?.endTime || ''}`;
        const conflictCompany = details?.workplaceTitle || 'Başka bir işyeri';

        Swal.fire({
          icon: 'warning',
          title: '⚠️ Ziyaret Saati Çakışması!',
          html: `
            <div style="text-align: left; font-size: 13px; line-height: 1.6; color: #1e293b; background: #fffbeb; padding: 12px; border-radius: 8px; border: 1.5px solid #fde047; margin-bottom: 10px;">
              <p style="margin: 0 0 8px 0; font-weight: 800; color: #b45309; font-size: 13.5px;">
                Belirttiğiniz gün ve saat aralığında başka bir firmanızda zaten ziyaret planlanmıştır:
              </p>
              <div style="margin-bottom: 5px;">📅 <strong>Çakışan Gün (Tarih):</strong> <span style="color: #dc2626; font-weight: 700;">${conflictDateStr}</span></div>
              <div style="margin-bottom: 5px;">⏰ <strong>Çakışan Saat Aralığı:</strong> <span style="color: #dc2626; font-weight: 700;">${conflictHoursStr}</span></div>
              <div style="margin-bottom: 5px;">🏢 <strong>Mevcut Planlı Firma:</strong> <span style="color: #0f172a; font-weight: 700;">${conflictCompany}</span></div>
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #f59e0b; color: #334155;">
                🕒 <strong>Yeni İstenen Saat:</strong> <strong>${startTime} - ${endTime}</strong> (${selectedWp.title})
              </div>
            </div>
            <p style="margin: 0; font-size: 12px; color: #64748b;">
              Saati değiştirmek için <strong>"⏰ Saati Değiştir"</strong> seçeneğini kullanabilir ya da çakışmaya rağmen bu saate kaydetmek için <strong>"⚠️ Yine de Kaydet"</strong> butonuna tıklayabilirsiniz.
            </p>
          `,
          showCancelButton: true,
          confirmButtonColor: '#d97706',
          cancelButtonColor: '#475569',
          confirmButtonText: '⚠️ Yine de Kaydet',
          cancelButtonText: '⏰ Saati Değiştir',
          reverseButtons: true
        }).then((result) => {
          if (result.isConfirmed) {
            saveVisit(true); // Çakışmayı onaylayarak zorla kaydet
          }
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Kayıt Başarısız',
          text: err.response?.data?.message || 'Ziyaret planlanırken bir hata oluştu.',
          confirmButtonColor: '#dc2626'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveVisit(false);
  };

  return (
    <div className="plan-visit-overlay" onClick={onClose}>
      <div className="plan-visit-modal" onClick={(e) => e.stopPropagation()}>
        {/* Üst Başlık Barı */}
        <div className="plan-visit-header">
          <div className="header-left">
            <span className="header-badge">📅 SAHA ZİYARETİ PLANLAMA</span>
            <div className="header-title-row">
              <h4 className="header-title">
                {selectedWp ? selectedWp.title : 'Yeni Saha Ziyareti Oluştur'}
              </h4>
              {selectedWp && (() => {
                const badge = getHazardBadge(selectedWp.dangerClass);
                return (
                  <span
                    className="selected-wp-hazard"
                    style={{
                      color: badge.color,
                      background: badge.bg,
                      borderColor: badge.border,
                      fontSize: '10px',
                      padding: '1px 6px',
                      lineHeight: '1.2'
                    }}
                  >
                    {badge.label}
                  </span>
                );
              })()}
            </div>
            {selectedWp && (
              <div className="header-sub-info">
                {selectedWp.city && <span>📍 {selectedWp.city}{selectedWp.district ? `/${selectedWp.district}` : ''}</span>}
                <span>👥 {selectedWp.employeeCount || 0} Çalışan</span>
                {selectedWp.sgkNo && <span>🔢 SGK: {selectedWp.sgkNo}</span>}
              </div>
            )}
          </div>
          <button type="button" className="btn-modal-close" onClick={onClose} title="Kapat">
            ✕
          </button>
        </div>

        {/* Ziyaret Formu */}
        <form onSubmit={handleSubmit} className="plan-visit-form">
          <div className="form-grid">
            {/* AKILLI İŞYERİ SEÇİM ALANI (Yalnızca serbest seçim modunda gösterilir) */}
            {canChangeWorkplace && (
              <div className="form-group full">
                <label>
                  Ziyaret Edilecek İşyeri / Firma * <span className="label-hint">(Akıllı Arama)</span>
                </label>

                {selectedWp ? (
                  /* Seçili İşyeri Bilgi Kartı */
                  <div className="plan-selected-wp-card">
                  <div className="selected-wp-info">
                    <div className="selected-wp-header-line">
                      <strong className="selected-wp-name">🏢 {selectedWp.title}</strong>
                      {(() => {
                        const badge = getHazardBadge(selectedWp.dangerClass);
                        return (
                          <span
                            className="selected-wp-hazard"
                            style={{
                              color: badge.color,
                              background: badge.bg,
                              borderColor: badge.border
                            }}
                          >
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="selected-wp-sub-line">
                      {selectedWp.city && <span>📍 {selectedWp.city} {selectedWp.district ? `/${selectedWp.district}` : ''}</span>}
                      <span>👥 {selectedWp.employeeCount || 0} Çalışan</span>
                      {selectedWp.sgkNo && <span>🔢 SGK: {selectedWp.sgkNo}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-change-wp"
                    onClick={handleChangeSelectedWorkplace}
                    title="Başka bir işyeri seç"
                  >
                    🔄 Değiştir
                  </button>
                </div>
              ) : (
                /* Akıllı Arama Giriş Kutusu */
                <div className="plan-wp-search-wrapper">
                  <div className="plan-wp-search-input-box">
                    <span className="search-box-icon">🔍</span>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="plan-wp-search-input"
                      placeholder="İşyeri unvanı veya SGK sicil no ile arayın..."
                      value={wpSearchTerm}
                      onChange={(e) => {
                        setWpSearchTerm(e.target.value);
                        setShowWpSuggestions(true);
                      }}
                      onFocus={() => setShowWpSuggestions(true)}
                    />
                    {wpSearchTerm && (
                      <button
                        type="button"
                        className="search-box-clear"
                        onClick={() => {
                          setWpSearchTerm('');
                          searchInputRef.current?.focus();
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Canlı Öneri Açılır Listesi */}
                  {showWpSuggestions && (
                    <div className="plan-wp-suggestions-dropdown">
                      <div className="suggestions-header">
                        <span>{wpSearchTerm ? 'Arama Sonuçları' : 'Kayıtlı İşyerleri (Seçiniz)'}</span>
                        <span className="suggestions-count">({suggestions.length} firma)</span>
                      </div>

                      {suggestions.length === 0 ? (
                        <div className="no-suggestion-item">
                          "{wpSearchTerm}" kriterine uygun işyeri bulunamadı.
                        </div>
                      ) : (
                        suggestions.map((wp) => {
                          const badge = getHazardBadge(wp.dangerClass);
                          return (
                            <div
                              key={wp.id}
                              className="plan-wp-suggestion-item"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelectWorkplace(wp);
                              }}
                            >
                              <div className="sug-main">
                                <strong className="sug-title">🏢 {wp.title}</strong>
                                <div className="sug-meta">
                                  {wp.city && <span>📍 {wp.city} {wp.district ? `/${wp.district}` : ''}</span>}
                                  <span>👥 {wp.employeeCount || 0} Çalışan</span>
                                  {wp.sgkNo && <span>SGK: {wp.sgkNo}</span>}
                                </div>
                              </div>
                              <span
                                className="sug-hazard-badge"
                                style={{
                                  color: badge.color,
                                  background: badge.bg,
                                  borderColor: badge.border
                                }}
                              >
                                {badge.label}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Ziyaret Tarihi (Takvim Seçici) */}
            <div className="form-group full">
              <label>
                Ziyaret Tarihi * <span className="label-hint">(Takvimden Tarih Seçin)</span>
              </label>
              <input
                type="date"
                required
                className={`plan-input date-picker ${isPastDate ? 'plan-input-past' : ''}`}
                value={visitDate}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setVisitDate(newDate);
                  if (newDate && newDate < today) {
                    setMarkAsCompleted(true);
                  } else {
                    setMarkAsCompleted(false);
                  }
                }}
              />

              {/* Geçmiş Tarih Akıllı Uyarı & Doğrudan Tamamlama Bandı */}
              {isPastDate && (
                <div className="plan-past-date-banner">
                  <div className="past-date-banner-header">
                    <span className="past-date-icon">⚠️</span>
                    <div className="past-date-text">
                      <strong>Geçmiş Tarihli Planlama:</strong> Bugünün öncesinde bir tarih seçtiniz ({pastDaysLabel})
                    </div>
                  </div>
                  <label className="past-date-checkbox-label">
                    <input
                      type="checkbox"
                      checked={markAsCompleted}
                      onChange={(e) => setMarkAsCompleted(e.target.checked)}
                    />
                    <span>
                      Bu ziyaret gerçekleşti (Doğrudan <strong>"Tamamlandı"</strong> olarak kaydet)
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Saat Aralığı */}
            <div className="form-group">
              <label>Başlangıç Saati</label>
              <input
                type="time"
                className="plan-input"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Bitiş Saati</label>
              <input
                type="time"
                className="plan-input"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </div>

            {/* Ziyaret Türü */}
            <div className="form-group full">
              <label>Ziyaret / Denetim Türü</label>
              <select
                className="plan-select"
                value={visitType}
                onChange={(e) => setVisitType(e.target.value)}
              >
                <option value="Periyodik Saha Ziyareti (Rutin)">📋 Periyodik Saha Ziyareti (Rutin)</option>
                <option value="Özel İSG Saha Denetimi">🔍 Özel İSG Saha Denetimi</option>
                <option value="Risk Değerlendirme Saha İncelemesi">🛡️ Risk Değerlendirme Saha İncelemesi</option>
                <option value="Acil Durum & Tatbikat Denetimi">🚨 Acil Durum &amp; Tatbikat Denetimi</option>
                <option value="İş Kazası & Olay İncelemesi">⚠️ İş Kazası &amp; Olay İncelemesi</option>
                <option value="Saha Çalışan Eğitimi">🎓 Saha Çalışan Eğitimi</option>
              </select>
            </div>

            {/* Ziyaret Konusu / Özel Notlar */}
            <div className="form-group full">
              <label>Ziyaret Konusu &amp; Saha Notları</label>
              <textarea
                className="plan-textarea"
                rows={3}
                placeholder="Örn: 2. Kat üretim hattı acil durdurma butonları, yangın tüpleri kontrolü ve çalışan İSG oryantasyonu..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* Alt Butonlar */}
          <div className="plan-visit-footer">
            <button
              type="button"
              className="btn-plan-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Vazgeç
            </button>
            <button
              type="submit"
              className={`btn-plan-save ${isPastDate && markAsCompleted ? 'btn-plan-save-completed' : ''}`}
              disabled={submitting}
            >
              {submitting
                ? 'Kaydediliyor...'
                : isPastDate && markAsCompleted
                ? 'Ziyareti Gerçekleşti Olarak Kaydet ✅'
                : 'Ziyareti Planla & Kaydet 💾'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanVisitModal;
